/** Display helpers shared by the four insight screens. Pure. */

export function formatNumber(value: number): string {
  return new Intl.NumberFormat("en-PH").format(value);
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  } catch {
    return `${currency} ${formatNumber(amount)}`;
  }
}

/** "PHP 3,500" style list for one or more currencies; "None yet" when empty. */
export function formatMoneyList(items: { currency: string; amount: number }[], empty = "None yet"): string {
  if (items.length === 0) return empty;
  return items.map((i) => formatMoney(i.amount, i.currency)).join(" + ");
}

export function formatPercent(fraction: number | null, digits = 0): string {
  if (fraction === null || !Number.isFinite(fraction)) return "No data";
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** For values the API already returns as a 0-100 percentage. */
export function formatPercentPoints(points: number | null, digits = 1): string {
  if (points === null || !Number.isFinite(points)) return "No data";
  return `${points.toFixed(digits)}%`;
}

export function formatHours(hours: number | null): string {
  if (hours === null || !Number.isFinite(hours)) return "No data";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} days`;
}

export function formatMinutes(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes)) return "No data";
  if (minutes < 90) return `${Math.round(minutes)} min`;
  return formatHours(minutes / 60);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`;
}

export function formatShortDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return d.toLocaleDateString("en-PH", { month: "short", day: "numeric" });
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleString("en-PH", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function formatAgo(iso: string | null, now: number = Date.now()): string {
  if (!iso) return "Never";
  const seconds = Math.max(0, Math.round((now - new Date(iso).getTime()) / 1000));
  return formatDuration(seconds) + " ago";
}

export function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172_800) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86_400)} days`;
}

/** Change between two counts as a fraction, or null when the base is zero. */
export function changeFraction(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return (current - previous) / previous;
}

export function sumBy<T>(items: readonly T[], pick: (item: T) => number): number {
  return items.reduce((total, item) => total + pick(item), 0);
}

export function formatLabel(value: string): string {
  const spaced = value.replace(/[._]/g, " ").replace(/\s+/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

export function formatHour(hour: number): string {
  const suffix = hour < 12 ? "am" : "pm";
  const h = hour % 12 === 0 ? 12 : hour % 12;
  return `${h}${suffix}`;
}
